import BooleanTag from '@/components/app/BooleanTag';
import ExportBtn from '@/components/app/ExportBtn';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import ZTable, { IFilterItem, IColumnType, ITableRef } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { exportService } from '@/services/exportService';
import { Button, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select, Space, Tag, Tooltip } from 'antd';
import { t } from 'i18next';
import { sumBy } from 'lodash';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { UnlockOutlined, SaveOutlined, CheckOutlined, LockOutlined, CloseOutlined } from '@ant-design/icons';
import { CompanyModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { ModalWidth, TimeZoneOptions, VALIDATE_REQUIRED } from '@/config';
import CreateBtn from '@/components/app/CreateBtn';
import CityInput from '@/components/FormItems/CityInput';
import CountryInput from '@/components/FormItems/CountryInput';
import RegionInput from '@/components/FormItems/RegionInput';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import PhoneCodeInput from '@/components/FormItems/PhoneCodeInput';

const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'searchText', mdSize: 8, xlSize: 8, xxlSize: 8, label: 'companyPage_SearchText' }];

const PAGE_TITLE = 'companyPage_Title';
const columns: IColumnType<CompanyModel>[] = [
  {
    title: 'companyPage_CompanyName',
    dataIndex: 'name',
    sorter: true,
    ellipsis: true,
    width: 200,
  },
  {
    title: 'base.PhoneNumber',
    dataIndex: 'phoneNo',
    width: 120,
    ellipsis: true,
    sorter: true,
  },
  {
    title: 'companyPage_PlanName',
    dataIndex: 'planName',
    width: 150,
    render: v => <Tag>{v}</Tag>,
  },
  {
    title: 'companyPage_NoOfStations',
    dataIndex: 'noOfStations',
    width: 120,
    ellipsis: true,
  },
  {
    title: 'companyPage_CityRegionCountry',
    dataIndex: 'countryName',
    width: 200,
    ellipsis: true,
    render: (v, record) => [record.cityName, record.regionName, record.countryName].filter(x => x).join(', '),
  },
  {
    title: 'base.Address',
    dataIndex: 'address',
    width: 100,
    ellipsis: true,
  },
  {
    title: 'companyPage_EmployeeNo',
    dataIndex: 'employerNo',
    width: 100,
    ellipsis: true,
  },

  {
    title: 'base.Active',
    dataIndex: 'isActive',
    width: 80,
    align: 'center',
    render: v => <BooleanTag value={v} />,
    sorter: true,
  },
  {
    title: 'companyPage_SMS_Email_NotificationAllow',
    dataIndex: 'isSmsNotificationAllowed',
    width: 170,
    align: 'center',
    render: (v, record) => (
      <Space>
        SMS
        <BooleanTag value={v} />
        Email
        <BooleanTag value={record.isEmailNotificationAllowed} />
      </Space>
    ),
  },
  {
    title: 'companyPage_IsApproved',
    dataIndex: 'isApproved',
    width: 120,
    align: 'center',
    render: v => <BooleanTag value={v} />,
    sorter: true,
  },
];

const Width = sumBy(columns, x => x.width || 0);

export default function CompaniesPage() {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [formVisible, setFormVisible] = useState(false);
  const [form] = Form.useForm();

  React.useEffect(() => {
    apiSdk.CompanyService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);
  const onExportExcel = (cols: string[]) => {
    exportService
      .exportCompanies({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
      })
      .toExcel();
  };
  const onExportPdf = (cols: string[]) => {
    exportService
      .exportCompanies({
        searchText: zTb.current?.getFilter()?.searchText,
        columns: cols,
      })
      .toPdf();
  };
  const activeTicketSelling = (id: string, active: boolean) => {
    setLoading(true);
    apiSdk.CompanyService.activeTicketSelling({
      body: {
        id: id,
        isTicketSellingEnabled: active,
      },
    })
      .then(() => {})
      .catch(defaultApiErrorHandle)

      .finally(() => {
        zTb.current?.reload();
        setLoading(false);
      });
  };
  const lockOrUnlockCompany = (id: string, lock: boolean) => {
    setLoading(true);
    apiSdk.CompanyService.blockUnblock({
      body: {
        id: id,
        isActive: lock,
      },
    })
      .then(() => {})
      .catch(defaultApiErrorHandle)

      .finally(() => {
        zTb.current?.reload();
        setLoading(false);
      });
  };
  const createNewCompany = (values: any) => {
    setLoading(true);
    apiSdk.CompanyService.register({
      body: values,
    })
      .then(res => {
        if (res.success) {
          zTb.current?.reload();
          setFormVisible(false);
        } else {
          defaultApiErrorHandle(res);
        }
      })
      .catch(defaultApiErrorHandle)
      .finally(() => {
        setLoading(false);
      });
  };

  const countryId = Form.useWatch('countryId', form);
  const regionId = Form.useWatch('regionId', form);

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      isLoading={loading}
      extraBtns={[
        <CreateBtn
          onClick={() => {
            form.resetFields();
            setFormVisible(true);
          }}
        />,
      ]}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<CompanyModel>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/commons/companies/get-companies"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={false}
          allowDelete={false}
          filters={filters}
          scroll={{ x: Width }}
          searchBtnAlignLeft
          actionColumnWidth={100}
          extraBtns={
            <ExportBtn
              disabled={!exportColumns?.length}
              onClick={() => {
                toggleExportModal(true);
              }}
            />
          }
          extraActions={[
            {
              render: record => {
                return (
                  <Tooltip title={t(record.isApproved ? 'companyPage_InActiveTicketSelling' : 'companyPage_ActiveTicketSelling')}>
                    <Button
                      loading={loading}
                      style={{ borderRadius: 0 }}
                      type="primary"
                      size="small"
                      danger={!record.isApproved}
                      icon={record.isApproved ? <CheckOutlined /> : <CloseOutlined />}
                      onClick={() => {
                        activeTicketSelling(record.id || '', !record.isApproved);
                      }}
                    ></Button>
                  </Tooltip>
                );
              },
            },
            {
              render: record => {
                return (
                  <Tooltip title={t(record.isActive ? 'Lock' : 'Unlock')}>
                    <Button
                      loading={loading}
                      style={{ borderRadius: 0 }}
                      type="primary"
                      size="small"
                      danger={!record.isActive}
                      icon={record.isActive ? <UnlockOutlined /> : <LockOutlined />}
                      onClick={() => {
                        lockOrUnlockCompany(record.id || '', !record.isActive);
                      }}
                    ></Button>
                  </Tooltip>
                );
              },
            },
          ]}
        ></ZTable>
      </div>
      {showExportModal ? (
        <ExportColumnsSelectorModal
          title={t('companyPage_ExportCompanies')}
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
      <Modal
        open={formVisible}
        maskClosable={false}
        width={ModalWidth.small}
        style={{ top: 10 }}
        className="nta-custom-form"
        title={'Create new company'}
        forceRender={true}
        okText={t('save')}
        cancelText={t('cancel')}
        okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
        onCancel={() => {
          form.resetFields();
          setFormVisible(false);
        }}
        onOk={() => {
          form
            .validateFields()
            .then(values => {
              createNewCompany(values);
            })
            .catch(info => {});
        }}
      >
        <Form form={form} size="middle" layout="horizontal" labelCol={{ md: 8 }}>
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={6}>
            <Col md={24}>
              <Form.Item label={t('Company Name')} name="storeName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Carrier Code')} name="carrierCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>

              <Form.Item label={t('base.FirstName')} name="firstName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('base.LastName')} name="lastName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('base.Email')} name="email" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('base.PhoneCode')} name="phoneCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <PhoneCodeInput />
              </Form.Item>
              <Form.Item label={t('base.PhoneNumber')} name="phoneNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('base.Password')} name="password" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input.Password />
              </Form.Item>
              <Form.Item
                label={t('Confirm password')}
                name="confirmPassword"
                rules={[
                  { required: true, message: VALIDATE_REQUIRED },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(t('updatePassword_PasswordNotMatch'));
                    },
                  }),
                ]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item label={t('Address')} name="address" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input.TextArea />
              </Form.Item>
              <Form.Item label={t('base.Country')} name="countryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <CountryInput
                  isOperatingOnly={false}
                  onChange={() => {
                    form.setFieldValue('regionId', null);
                  }}
                />
              </Form.Item>
              <Form.Item label={t('base.Region')} name="regionId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <RegionInput
                  disabled={!countryId}
                  countryId={countryId}
                  onChange={() => {
                    form.setFieldValue('cityId', null);
                  }}
                />
              </Form.Item>
              <Form.Item label={t('base.City')} name="cityId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <CityInput regionId={regionId} disabled={!regionId} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </MainContent>
  );
}
